import { useEffect, useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { useCurrentWell } from "@hooks/useCurrentWell";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import {
  pApi,
  useGetEventReportsQuery,
  useCreateReportMutation,
  useUpdateReportMutation,
  useUpdateArchMetaMutation,
  useCloseReportMutation,
} from "@api/pApi";
import { useAppDispatch } from "@app/hooks";
import { fmtNum, fmtPct } from "@lib/format";
import { useUnits } from "@units/useUnits";
import { useMoney } from "@units/useMoney";
import type { DailyReportOut } from "@api/types";
import { NptPanel } from "@features/timeNpt/NptPanel";
import { TimeEntryPanel } from "@features/timeNpt/TimeEntryPanel";
import { CostPanel } from "@features/costAfe/CostPanel";
import { ArchReportPanel } from "@features/archReport/ArchReportPanel";

/**
 * Seed a depth input from a stored value at 1 decimal — never the raw float
 * (e.g. 15517.388390450002). Editing is unaffected; this only trims the initial
 * display. Empty when the value is absent.
 */
function depthInput(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? "" : (Math.round(value * 10) / 10).toString();
}

type Tab = "general" | "timeNpt" | "cost" | "personnel" | "attachments";
const TABS: { id: Tab; key: Parameters<ReturnType<typeof useLocale>["t"]>[0] }[] = [
  { id: "general", key: "ddr.tab.general" },
  { id: "timeNpt", key: "ddr.tab.timeNpt" },
  { id: "cost", key: "ddr.tab.cost" },
  { id: "personnel", key: "ddr.tab.personnel" },
  { id: "attachments", key: "ddr.tab.attachments" },
];

/**
 * Daily Drilling Report — the job's home screen, one day per report. Tabbed
 * (General · Time/NPT · Cost · Personnel · Attachments). KPIs (day, MD/TVD,
 * ROP, % complete, daily cost) are SERVER-COMPUTED by Oxbo-P and shown
 * verbatim — the FE never recomputes them. Create via POST /v1/reports, edit
 * via PATCH, with a carry-over affordance that seeds the new report from the
 * previous day's depths.
 */
export function DailyReport() {
  const { t, locale } = useLocale();
  const { well, activeEvent, isLoading: ctxLoading, isError: ctxError } = useCurrentWell();

  const eventId = activeEvent?.id ?? "";
  const {
    data: reports = [],
    isLoading,
    isError,
    refetch,
  } = useGetEventReportsQuery(eventId, { skip: !eventId });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("general");

  // Default to the newest report when the list loads / changes.
  const selected = useMemo<DailyReportOut | undefined>(() => {
    if (selectedId) return reports.find((r) => r.id === selectedId) ?? reports[0];
    return reports[0];
  }, [reports, selectedId]);

  return (
    <div>
      <div className="page-head">
        <h1>{t("ddr.title")}</h1>
        <p className="subtitle">
          {well ? `${well.commonName} · ${well.field}` : t("well.notSelected")}
        </p>
      </div>

      <QueryState
        isLoading={ctxLoading || isLoading}
        isError={ctxError || isError}
        isEmpty={!activeEvent}
        emptyMessage={t("well.event.noEvents")}
        onRetry={refetch}
      >
        {reports.length === 0 ? (
          <EmptyWithCreate eventId={eventId} wellUwi={well?.uwi ?? ""} reports={reports} />
        ) : (
          <>
            {/* report selector + actions */}
            <div className="ddr-toolbar">
              <div className="field">
                <label htmlFor="ddr-report">{t("ddr.selectReport")}</label>
                <select
                  id="ddr-report"
                  value={selected?.id ?? ""}
                  onChange={(e) => setSelectedId(e.target.value)}
                >
                  {reports.map((r) => {
                    const isLatest = r.id === reports[0]?.id;
                    return (
                      <option key={r.id} value={r.id}>
                        {isLatest ? "★ " : ""}
                        {t("ddr.day", { n: r.report_no })} · {r.date_report} ·{" "}
                        {t(r.state === "closed" ? "state.closed" : "state.open")}
                        {isLatest ? ` · ${t("ddr.latest")}` : ""}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="ddr-actions">
                <CreateReportButton
                  eventId={eventId}
                  wellUwi={well?.uwi ?? ""}
                  reports={reports}
                  onCreated={setSelectedId}
                />
                {selected && selected.state !== "closed" ? (
                  <CloseReportButton report={selected} />
                ) : null}
              </div>
            </div>

            {selected ? (
              <>
                {/* Server-computed KPI band */}
                <KpiBand report={selected} locale={locale} />

                {/* tabs */}
                <div className="tabbar" role="tablist" aria-label={t("ddr.title")}>
                  {TABS.map((tb) => (
                    <button
                      key={tb.id}
                      type="button"
                      role="tab"
                      aria-selected={tab === tb.id}
                      className={tab === tb.id ? "active" : ""}
                      onClick={() => setTab(tb.id)}
                    >
                      {t(tb.key)}
                    </button>
                  ))}
                </div>

                <div className="tab-panel" role="tabpanel">
                  {tab === "general" ? <GeneralTab report={selected} /> : null}
                  {tab === "timeNpt" ? (
                    <>
                      <TimeEntryPanel
                        reportId={selected.id}
                        eventId={eventId}
                        reportDate={selected.date_report}
                        unitProfileKey={selected.unit_profile_key}
                        readOnly={selected.state === "closed"}
                      />
                      <div style={{ marginTop: 16 }}>
                        <NptPanel eventId={eventId} />
                      </div>
                    </>
                  ) : null}
                  {tab === "cost" ? (
                    <CostPanel
                      eventId={eventId}
                      reportId={selected.id}
                      reportDate={selected.date_report}
                    />
                  ) : null}
                  {tab === "personnel" ? (
                    <div className="card">
                      <div className="empty-state">{t("ddr.personnel.placeholder")}</div>
                    </div>
                  ) : null}
                  {tab === "attachments" ? (
                    <div className="card">
                      <div className="empty-state">{t("ddr.attachments.placeholder")}</div>
                    </div>
                  ) : null}
                </div>

                {/* ARCH regulatory report — workover events only. Collapsed by
                    default; gathers the manual fields + generates the xlsx. */}
                {activeEvent?.event_type === "workover" ? (
                  <ArchReportPanel
                    report={selected}
                    event={activeEvent}
                    readOnly={selected.state === "closed"}
                  />
                ) : null}
              </>
            ) : null}
          </>
        )}
      </QueryState>
    </div>
  );
}

function KpiBand({
  report,
  locale,
}: {
  report: DailyReportOut;
  locale: ReturnType<typeof useLocale>["locale"];
}) {
  const { t } = useLocale();
  // Resolve every measurement from its stored profile (the report's own
  // unit_profile_key) into the viewer's display profile, with a DYNAMIC label.
  const { convertField, fmt, fmtDepth } = useUnits(report.unit_profile_key);
  const { money } = useMoney();

  const profile = report.unit_profile_key;
  const rop = convertField("daily_report", "rop_avg_day", report.rop_avg_day, profile);

  const stats: Stat[] = [
    {
      label: t("ddr.kpi.day"),
      value: fmtNum(report.days_op ?? report.report_no, locale),
      sub: report.days_total ? `${t("common.of")} ${report.days_total}` : undefined,
    },
    // Depth: rendered via fmtDepth — feet at 1 decimal on the demo (no org UOM
    // profile), or the resolved profile's unit when one exists.
    { label: t("ddr.kpi.md"), value: fmtDepth(report.md_current), tone: "ops" },
    { label: t("ddr.kpi.tvd"), value: fmtDepth(report.tvd_current), tone: "ops" },
    {
      label: t("ddr.kpi.rop"),
      value: fmt(rop, { maximumFractionDigits: 1 }),
      tone: "ops",
    },
    { label: t("ddr.kpi.pct"), value: fmtPct(report.pct_complete, locale), tone: "good" },
    {
      label: t("ddr.kpi.cost"),
      value: money(report.daily_cost, report.currency, { asOf: report.date_report }),
      tone: "ops",
    },
  ];
  return <StatBand stats={stats} />;
}

/** General tab — editable summary/forecast + editable current depths (PATCH). */
function GeneralTab({ report }: { report: DailyReportOut }) {
  const { t } = useLocale();
  const { convertField } = useUnits(report.unit_profile_key);
  const [updateReport, { isLoading }] = useUpdateReportMutation();

  // Dynamic depth unit for the input labels; falls back to feet on the demo
  // (no org UOM profile → convertField returns an empty unit).
  const depthUnit =
    convertField("daily_report", "md_current", report.md_current ?? 0, report.unit_profile_key)
      .unit || "ft";
  const withUnit = (label: string) => `${label} (${depthUnit})`;

  const [summary, setSummary] = useState(report.comment_summary ?? "");
  const [forecast, setForecast] = useState(report.comment_forecast_24 ?? "");
  const [md, setMd] = useState(depthInput(report.md_current));
  const [tvd, setTvd] = useState(depthInput(report.tvd_current));

  // Re-seed the form when the selected report changes.
  useEffect(() => {
    setSummary(report.comment_summary ?? "");
    setForecast(report.comment_forecast_24 ?? "");
    setMd(depthInput(report.md_current));
    setTvd(depthInput(report.tvd_current));
  }, [
    report.id,
    report.comment_summary,
    report.comment_forecast_24,
    report.md_current,
    report.tvd_current,
  ]);

  const readOnly = report.state === "closed";
  const dirty =
    summary !== (report.comment_summary ?? "") ||
    forecast !== (report.comment_forecast_24 ?? "") ||
    md !== (report.md_current?.toString() ?? "") ||
    tvd !== (report.tvd_current?.toString() ?? "");

  const save = () => {
    void updateReport({
      id: report.id,
      body: {
        comment_summary: summary || null,
        comment_forecast_24: forecast || null,
        md_current: md ? Number(md) : null,
        tvd_current: tvd ? Number(tvd) : null,
      },
    });
  };

  return (
    <div className="grid-2">
      <div className="card">
        <h3>
          {t("ddr.summary")} <span className="tag-op">{t("ddr.tab.general")}</span>
        </h3>
        {readOnly && !summary ? (
          <p className="empty-state" style={{ textAlign: "left" }}>
            {t("ddr.summaryPlaceholder")}
          </p>
        ) : (
          <textarea
            className="text-area"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            readOnly={readOnly}
            aria-label={t("ddr.summary")}
            placeholder={t("ddr.summaryPlaceholder")}
            rows={7}
          />
        )}
        <h3 style={{ marginTop: 14 }}>{t("ddr.forecast")}</h3>
        {readOnly && !forecast ? (
          <p className="empty-state" style={{ textAlign: "left" }}>
            {t("ddr.forecastPlaceholder")}
          </p>
        ) : (
          <textarea
            className="text-area"
            value={forecast}
            onChange={(e) => setForecast(e.target.value)}
            readOnly={readOnly}
            aria-label={t("ddr.forecast")}
            placeholder={t("ddr.forecastPlaceholder")}
            rows={5}
          />
        )}
      </div>
      <div className="card">
        <h3>{t("ddr.tab.general")}</h3>
        <div className="field" style={{ marginBottom: 12 }}>
          <label htmlFor="ddr-md">{withUnit(t("ddr.field.mdCurrent"))}</label>
          <input
            id="ddr-md"
            type="number"
            inputMode="decimal"
            value={md}
            onChange={(e) => setMd(e.target.value)}
            readOnly={readOnly}
          />
        </div>
        <div className="field" style={{ marginBottom: 12 }}>
          <label htmlFor="ddr-tvd">{withUnit(t("ddr.field.tvdCurrent"))}</label>
          <input
            id="ddr-tvd"
            type="number"
            inputMode="decimal"
            value={tvd}
            onChange={(e) => setTvd(e.target.value)}
            readOnly={readOnly}
          />
        </div>
        {!readOnly ? (
          <button
            type="button"
            className="btn primary"
            disabled={!dirty || isLoading}
            onClick={save}
          >
            {isLoading ? t("ddr.saving") : t("ddr.save")}
          </button>
        ) : (
          <span className="badge">{t("ddr.closed")}</span>
        )}
        <p className="hint" style={{ marginTop: 10 }}>
          {t("ddr.computed")}: {t("ddr.kpi.day")}, {t("ddr.kpi.rop")}, {t("ddr.kpi.pct")},{" "}
          {t("ddr.kpi.cost")} — {t("npt.subtitle")}
        </p>
      </div>
    </div>
  );
}

/** Compute the next report number and its previous report for carry-over. */
function nextReportInfo(reports: DailyReportOut[]) {
  const maxNo = reports.reduce((m, r) => Math.max(m, r.report_no), 0);
  const prev = reports.find((r) => r.report_no === maxNo);
  return { nextNo: maxNo + 1, prev };
}

function CreateReportButton({
  eventId,
  wellUwi,
  reports,
  onCreated,
}: {
  eventId: string;
  wellUwi: string;
  reports: DailyReportOut[];
  onCreated: (id: string) => void;
}) {
  const { t } = useLocale();
  const dispatch = useAppDispatch();
  const [createReport, { isLoading }] = useCreateReportMutation();
  const [updateMeta] = useUpdateArchMetaMutation();
  const [carryOver, setCarryOver] = useState(true);
  const { nextNo, prev } = nextReportInfo(reports);

  const create = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const body = {
      event_id: eventId,
      well_uwi: wellUwi,
      report_no: nextNo,
      date_report: today,
      // Carry-over: seed depths + supervisor from the previous report.
      ...(carryOver && prev
        ? {
            md_current: prev.md_current,
            tvd_current: prev.tvd_current,
            supervisor_1_entry_key: prev.supervisor_1_entry_key,
          }
        : {}),
    };
    const res = await createReport(body)
      .unwrap()
      .catch(() => null);
    if (res) {
      if (carryOver && prev) {
        // Carry the workover's manual ARCH header fields (campo/equipo/operador/
        // yacimiento/contratista/bloque/AFE Nro/… — all event-constant) to the new
        // parte. Day-specific entries (observaciones, test readings) start blank.
        try {
          const prevMeta = await dispatch(
            pApi.endpoints.getArchMeta.initiate(prev.id, { forceRefetch: true }),
          ).unwrap();
          await updateMeta({
            dailyReportId: res.id,
            body: {
              objetivo: prevMeta.objetivo,
              yacimiento: prevMeta.yacimiento,
              tipo_trabajo: prevMeta.tipo_trabajo,
              con_sin_torre: prevMeta.con_sin_torre,
              operator_nombre: prevMeta.operator_nombre,
              operator_cc: prevMeta.operator_cc,
              config_overrides: prevMeta.config_overrides,
              observaciones: { s4_3: null, s5_3: null, s6_1: null, s6_2: null, s7: null },
              test_readings: {},
            },
          }).unwrap();
        } catch {
          /* non-fatal: header fields can be re-entered in the Reporte ARCH panel */
        }
      }
      onCreated(res.id);
    }
  };

  return (
    <div className="create-report">
      <label className="carry-toggle">
        <input
          type="checkbox"
          checked={carryOver}
          onChange={(e) => setCarryOver(e.target.checked)}
          disabled={!prev}
        />
        {t("ddr.carryOver")}
      </label>
      <button type="button" className="btn primary" disabled={isLoading} onClick={create}>
        {isLoading ? t("ddr.creating") : `${t("ddr.create")} · ${t("ddr.day", { n: nextNo })}`}
      </button>
    </div>
  );
}

function EmptyWithCreate({
  eventId,
  wellUwi,
  reports,
}: {
  eventId: string;
  wellUwi: string;
  reports: DailyReportOut[];
}) {
  const { t } = useLocale();
  return (
    <div className="card">
      <div className="empty-state">
        {t("ddr.noReports")}
        <div style={{ marginTop: 14 }}>
          <CreateReportButton
            eventId={eventId}
            wellUwi={wellUwi}
            reports={reports}
            onCreated={() => undefined}
          />
        </div>
      </div>
    </div>
  );
}

function CloseReportButton({ report }: { report: DailyReportOut }) {
  const { t } = useLocale();
  const [closeReport, { isLoading }] = useCloseReportMutation();
  return (
    <button
      type="button"
      className="btn"
      disabled={isLoading}
      onClick={() => void closeReport({ id: report.id, isLastOfEvent: report.is_last_of_event })}
    >
      {t("ddr.close")}
    </button>
  );
}
